import { Avatar, Box, Button, Flex, Grid, GridItem, HStack, Icon, Spinner, Text, VStack } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiGlobe, FiLock, FiUsers } from "react-icons/fi";
import { getGroupPost, WallHeader, WallPost, WallVoting } from "@lubbezposrednio-frontend/core";
import { OrganizerPage } from "../OrganizerDashboardView/components/OrganizerShell";
import WallPostCard from "./WallPostCard";

const mediaUrl = (p?: string | null) => (p ? `${process.env.REACT_APP_BACKEND_URL}${p}` : undefined);

const statusMeta: Record<string, { label: string; color: string }> = {
  ongoing: { label: "Trwa", color: "#1f9e18" },
  planned: { label: "Zaplanowane", color: "#430098" },
  finished: { label: "Zakończone", color: "#94a3b8" },
  draft: { label: "Szkic", color: "#b45309" }
};

const SidebarCard: React.FC<{ title: string; children: React.ReactNode; action?: React.ReactNode }> = ({ title, children, action }) => (
  <Box bg="white" borderRadius="16px" border="1px solid rgba(67,0,152,0.08)" boxShadow="0 6px 20px rgba(67,0,152,0.05)" p="16px 18px">
    <Flex justify="space-between" align="center" mb="12px">
      <Text fontSize="13px" fontWeight="800" textTransform="uppercase" letterSpacing="0.04em" color="gray.400">{title}</Text>
      {action}
    </Flex>
    {children}
  </Box>
);

const VotingRow: React.FC<{ v: WallVoting }> = ({ v }) => {
  const s = statusMeta[v.status] || { label: v.status, color: "#94a3b8" };
  return (
    <HStack as={Link} to={`/glosowanie/${v.id}`} justify="space-between" bg="#FAF8FE" borderRadius="11px" p="9px 12px" _hover={{ bg: "rgba(67,0,152,0.06)" }}>
      <HStack spacing="9px" minW={0}>
        <Box w="8px" h="8px" borderRadius="full" bg={s.color} flexShrink={0} />
        <Text fontSize="13.5px" fontWeight="600" color="primary" noOfLines={1}>{v.title}</Text>
      </HStack>
      <Text fontSize="11.5px" fontWeight="700" color={s.color} flexShrink={0}>{s.label}</Text>
    </HStack>
  );
};

const GroupContextSidebar: React.FC<{ header: WallHeader }> = ({ header }) => (
  <VStack align="stretch" spacing="14px" position={{ lg: "sticky" }} top={{ lg: "16px" }}>
    <SidebarCard title="O grupie">
      <HStack spacing="11px" mb="10px">
        <Avatar size="md" name={header.name} src={mediaUrl(header.avatar_url)} bg="rgba(67,0,152,0.1)" color="primary" borderRadius="12px" />
        <Text fontWeight="800" color="primary" fontSize="15px" noOfLines={2}>{header.name}</Text>
      </HStack>
      <Text color="gray.600" fontSize="14px" whiteSpace="pre-wrap" noOfLines={5}>{header.description || "Brak opisu grupy."}</Text>
      <HStack spacing="6px" color="gray.500" fontSize="13px" mt="12px">
        <Icon as={header.visibility === "public" ? FiGlobe : FiLock} boxSize="13px" />
        <Text>{header.visibility === "public" ? "Publiczna" : "Prywatna"}</Text>
        <Text>·</Text>
        <Text>{header.members_count} {header.members_count === 1 ? "członek" : "członków"}</Text>
      </HStack>
      <Button as={Link} to={`/organizator/grupy/${header.id}`} mt="14px" w="100%" variant="outline" borderColor="primary" color="primary" borderRadius="11px" size="sm" fontWeight="700" _hover={{ bg: "rgba(67,0,152,0.05)" }}>
        Otwórz grupę
      </Button>
    </SidebarCard>

    <SidebarCard title="Członkowie">
      {header.members_preview.length === 0 ? (
        <Text color="gray.400" fontSize="13px">Brak członków.</Text>
      ) : (
        <Flex wrap="wrap" gap="10px">
          {header.members_preview.slice(0, 10).map((m, i) =>
            m.profile_ref ? (
              <Link key={i} to={`/profil/${m.profile_ref}`}>
                <Avatar size="sm" name={m.name} src={mediaUrl(m.avatar_url)} bg="rgba(67,0,152,0.1)" color="primary" />
              </Link>
            ) : (
              <Avatar key={i} size="sm" name={m.name} src={mediaUrl(m.avatar_url)} bg="rgba(67,0,152,0.1)" color="primary" />
            )
          )}
        </Flex>
      )}
    </SidebarCard>

    {header.votings.length > 0 && (
      <SidebarCard title="Głosowania">
        <VStack align="stretch" spacing="7px">{header.votings.slice(0, 4).map(v => <VotingRow key={v.id} v={v} />)}</VStack>
      </SidebarCard>
    )}
  </VStack>
);

const PostDetailView: React.FC = () => {
  const { groupId = "", postId = "" } = useParams<{ groupId: string; postId: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<WallPost | null>(null);
  const [header, setHeader] = useState<WallHeader | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    document.title = "Post";
    setLoading(true);
    getGroupPost(groupId, postId)
      .then(r => {
        setPost(r.data.post);
        setHeader(r.data.group);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [groupId, postId]);

  return (
    <OrganizerPage>
      <HStack as={Link} to={`/organizator/grupy/${groupId}`} spacing="8px" color="gray.500" fontSize="14px" fontWeight="600" mb="16px" _hover={{ color: "primary" }} w="fit-content">
        <FiArrowLeft />
        <Text>{header ? `Grupa „${header.name}”` : "Wróć do grupy"}</Text>
      </HStack>

      {loading ? (
        <Flex justify="center" py="80px"><Spinner color="primary" size="lg" thickness="3px" /></Flex>
      ) : error || !post || !header ? (
        <Flex direction="column" align="center" justify="center" py="70px" bg="white" borderRadius="20px" border="1px dashed rgba(67,0,152,0.2)">
          <Icon as={FiUsers} boxSize="26px" color="primary" mb="12px" />
          <Text color="primary" fontWeight="800" mb="4px">Nie znaleziono posta</Text>
          <Text color="gray.500" fontSize="14px" textAlign="center" maxW="360px">Post mógł zostać usunięty albo nie masz do niego dostępu.</Text>
          <Button as={Link} to={`/organizator/grupy/${groupId}`} mt="16px" bg="primary" color="white" borderRadius="11px" fontWeight="700" _hover={{ bg: "#360079" }}>
            Otwórz grupę
          </Button>
        </Flex>
      ) : (
        <Grid templateColumns={{ base: "1fr", lg: "1fr 320px" }} gap="16px" alignItems="start">
          <GridItem minW={0}>
            <WallPostCard
              post={post}
              groupId={groupId}
              isModerator={header.is_moderator}
              onChange={updater => setPost(p => (p ? updater(p) : p))}
              onRemove={() => navigate(`/organizator/grupy/${groupId}`)}
            />
          </GridItem>
          <GridItem display={{ base: "none", lg: "block" }}>
            <GroupContextSidebar header={header} />
          </GridItem>
        </Grid>
      )}
    </OrganizerPage>
  );
};

export default PostDetailView;
